import { useOutletContext } from 'react-router-dom'

import {
  Box,
  Button,
  Form,
  Icon,
  Section,
  SelectOptionGroups,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  TimeConditionsFeatureAvailabilitiesDocument,
  UpsertTimeConditionMutationVariables,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo, useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetTimeConditions } from 'modules/legal_entities/hooks/timeConditions/useGetTimeConditions'
import type { TimeCondition } from 'modules/legal_entities/hooks/timeConditions/useGetTimeConditions'
import { useUpsertTimeCondition } from 'modules/legal_entities/hooks/timeConditions/useUpsertTimeCondition'
import TimeConditionsLegalEntityForm from 'modules/legal_entities/lib/forms/TimeConditionsLegalEntityForm'
import {
  DEFAULT_DURATION,
  DEFAULT_START_ON,
} from 'modules/legal_entities/lib/forms/TimeConditionsLegalEntityForm'

const TimeConditionsLegalEntityPage = () => {
  const {
    legalEntity,
    loading: loadingLegalEntity,
    onViewActivity,
  } = useOutletContext<{
    legalEntity: LegalEntity
    loading: boolean
    onViewActivity: () => void
  }>()

  const { company } = useCurrentSession()

  const { data: featuresData } = useQuery(TimeConditionsFeatureAvailabilitiesDocument, {
    variables: { companyId: company.id },
  })

  const annualisationEnabled =
    featuresData?.companies?.featureAvailabilitiesConnection?.nodes?.find(
      (feature) => feature.id === 'time_tracking_annualisation_daily_value'
    )?.available ?? false

  const forfaitJoursEnabled =
    featuresData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'dev_forfaitjour_in_time_management'
    )?.enabled ?? false

  const {
    loading: loadingTimeConditions,
    error: errorsTimeConditions,
    timeConditions,
  } = useGetTimeConditions(legalEntity.id)
  const timeCondition: TimeCondition | undefined = timeConditions?.[0] ?? undefined
  const form = useUnsafeMemo(
    () =>
      new TimeConditionsLegalEntityForm({
        id: timeCondition?.id,
        legalEntityId: legalEntity?.id,
        startOn: timeCondition?.startOn ?? DEFAULT_START_ON,
        duration: timeCondition?.duration ?? DEFAULT_DURATION,
      }),
    [legalEntity, timeCondition]
  )

  const startOnField = form.get('startOn')
  const durationField = form.get('duration')

  const { upsertTimeCondition } = useUpsertTimeCondition()
  const onSubmit = () => {
    asyncAction(() => {
      const formValues: UpsertTimeConditionMutationVariables = {
        id: null,
        legalEntityId: String(legalEntity.id),
        startOn: Number(startOnField.value),
        duration: Number(durationField.value),
      }
      if (timeCondition !== undefined) {
        formValues['id'] = timeCondition.id
      }
      return upsertTimeCondition(formValues)
    }).then((result) => {
      if (result.success) {
        track(trackingEvents.legalEntities.legalEntityCycleChanged, {
          company_id: company.id,
          company_name: company.name,
          legal_entity_id: legalEntity.id,
          legal_entity_name: legalEntity.name,
          old_start_on: timeCondition?.startOn ?? DEFAULT_START_ON,
          new_start_on: startOnField.value,
          old_duration: timeCondition?.duration ?? DEFAULT_DURATION,
          new_duration: durationField.value,
          annualisation_enabled: annualisationEnabled,
          forfait_jours_enabled: forfaitJoursEnabled,
        })
      }
    })
  }

  const monthsList = useMemo<SelectOptionGroups>(() => {
    return moment.monthsShort().map((month, i) => ({
      value: i + 1,
      label: month,
    }))
  }, [])

  const error = errorsTimeConditions
  const loading = loadingLegalEntity || loadingTimeConditions

  return (
    <ResponsivePage.Body
      loading={loading}
      error={error}
      onActionClick={(key) => {
        if (key === 'view-activity') onViewActivity()
      }}
    >
      <Section.CustomTwoThirds
        primary={
          <Box gap="s8">
            <Icon.Large color="radicalRed1000" icon="timer" />
            <Text.Normal size="300" weight="medium" color="grey1000">
              {i18n.t('settings.legal_entities.legal_entity.settings.time_conditions.title')}
            </Text.Normal>
            <Text.Normal color="grey800" size="200">
              {i18n.t('settings.legal_entities.legal_entity.settings.time_conditions.description')}
            </Text.Normal>
          </Box>
        }
        secondary={
          <Form onSubmit={onSubmit} withBackground>
            <Text.Normal color="grey800" size="300" weight="medium">
              {i18n.t('settings.legal_entities.legal_entity.settings.time_conditions.cycle.title')}
            </Text.Normal>

            <SelectField
              label={i18n.t(
                'settings.legal_entities.legal_entity.settings.time_conditions.cycle.start_on'
              )}
              options={monthsList}
              value={startOnField.value}
              onChange={(startOn) => startOnField.set(startOn)}
              showOriginalLabelText
            />

            <TextField
              label={i18n.t(
                'settings.legal_entities.legal_entity.settings.time_conditions.cycle.duration'
              )}
              value={i18n.t(
                'settings.legal_entities.legal_entity.settings.time_conditions.cycle.duration_months',
                { months: durationField.value }
              )}
              onChange={(duration) => durationField.set(duration)}
              readonly
            />

            {form.isDirty && (
              <Form.Buttons>
                <Button disabled={!form.isValid} submit>
                  {i18n.t('common.submit')}
                </Button>
              </Form.Buttons>
            )}
          </Form>
        }
      />
    </ResponsivePage.Body>
  )
}

export default observer(TimeConditionsLegalEntityPage)
